import React, { useState } from 'react'

import { Box } from '@factorialco/deprecated-design-system'

import { observer } from 'mobx-react'

import Loading from 'modules/core/components/Loading'
import { useAsyncData } from 'modules/core/hooks/useAsyncData'
import { FormErrors, useForm } from 'modules/core/hooks/useForm'
import { track, trackingEvents } from 'modules/core/lib/Tracking'
import { dateToString, dateUIToBackend, stringToDate } from 'modules/core/lib/dates'
import { employeeLegalEntity } from 'modules/core/lib/employeeLegalEntity'
import { handleResult } from 'modules/core/lib/handleMutation'
import i18n from 'modules/core/lib/i18n'
import usersCollection, { employeeUser } from 'modules/core/store/collections/users'
import app from 'modules/core/store/models/app'

import { useUpdateEmployee } from 'modules/employees/hooks/useUpdateEmployee'
import { getDirtyValues } from 'modules/employees/lib/getDirtyValues'
import employees from 'modules/employees/store/collections/employees'
import employeesCollection from 'modules/employees/store/collections/employees'
import { identifierTypesCollection } from 'modules/employees/store/collections/identifierTypes'
import type Employee from 'modules/employees/store/models/Employee'
import { Country } from 'modules/legal_entities/lib/constants'
import idValidationStrategies from 'modules/legal_entities/lib/identifierValidators'
import { SpainIdType } from 'modules/legal_entities/lib/identifierValidators/spain/idType'
import type { IdentificationType } from 'modules/legal_entities/lib/types'
import genders from 'modules/settings/store/collections/genders'
import legalGenders from 'modules/settings/store/collections/legalGenders'

import FormSection from '../FormSection'
import IdentificationSection from './IdentificationSection'
import PersonalSection from './PersonalSection'
import { BasicInformationValues } from './types'

interface Props {
  employee: Employee
  canEdit: boolean
}

const BasicInformation: React.FC<Props> = ({ employee, canEdit }) => {
  const user = employeeUser(employee)
  const legalEntity = employeeLegalEntity(employee)
  const isSpainCompany = legalEntity.country?.toLowerCase() === 'es'
  const countryMetadata = employee.attributes.country_metadata
  const socialSecurityNumber = isSpainCompany ? countryMetadata?.social_security_number : null
  const hasWorkPermit = isSpainCompany ? countryMetadata?.has_work_permit : false
  const [identifiersDirty, setIdentifiersDirty] = useState(false)
  const [identifiersError, setIdentifiersError] = useState(false)
  const [[submitIdentifiersEventHandler], setSubmitIdentifiersEventHandler] = useState<
    [() => void] | [null]
  >([null])

  const { update, loading: submitting } = useUpdateEmployee()

  const countryEnumValue: Country = legalEntity.country as Country
  const { loading } = useAsyncData(
    () =>
      Promise.all([
        employees.fetch(),
        identifierTypesCollection.fetch({ data: { country: legalEntity?.country } }),
        genders.fetch(),
        legalGenders.fetch(),
      ]),
    []
  )

  const calculateIdentifierError = (
    identifier: string,
    identifierType: string,
    idTypeLabel: string
  ) => {
    return idValidationStrategies(
      countryEnumValue,
      identifierType as IdentificationType,
      identifier,
      idTypeLabel
    ).errors()
  }

  const calculateSocialSecurityNumberError = (value: string) => {
    return idValidationStrategies(
      countryEnumValue as Country,
      'social_security_number' as SpainIdType,
      value,
      'social_security_number'
    ).errors()
  }

  const idTypesOptions = identifierTypesCollection.toArray().map((item) => ({
    value: item.attributes.slug,
    label: item.attributes.label,
  }))

  const defaultBirthdayOn = () => {
    if (!employee.attributes.birthday_on) return ''

    return dateToString(stringToDate(employee.attributes.birthday_on, 'backend'), 'ui')
  }

  const form = useForm<BasicInformationValues>({
    defaultValues: {
      firstName: user.firstName,
      lastName: user.lastName,
      preferredName: employee.attributes.preferred_name ?? '',
      birthdayOn: defaultBirthdayOn(),
      nationality: employee.attributes.nationality ?? '',
      gender: employee.attributes.gender ?? '',
      identifier: employee.attributes.identifier ?? '',
      identifierType: employee.attributes.identifier_type ?? '',
      socialSecurityNumber: socialSecurityNumber,
      hasWorkPermit: hasWorkPermit,
      pronouns: employee.attributes.pronouns ?? '',
    },
    validate(data) {
      const errors: FormErrors<BasicInformationValues> = {}

      if (!data.firstName) {
        errors.firstName = i18n.t('employee_form.validations.empty')
      }

      if (!data.lastName) {
        errors.lastName = i18n.t('employee_form.validations.empty')
      }

      if (!data.nationality) {
        errors.nationality = i18n.t('employee_form.validations.empty')
      }

      if (!!data.identifier && !!data.identifierType) {
        const idTypeLabel = idTypesOptions.find((x) => data.identifierType === x.value)?.label
        const idErrors = calculateIdentifierError(data.identifier, data.identifierType, idTypeLabel)
        if (!!idErrors) {
          errors.identifier = idErrors
        }
      }

      if (!!data.identifier && !data.identifierType) {
        errors.identifierType = i18n.t('employee_form.validations.empty')
      }

      if (!!data.socialSecurityNumber) {
        const securityError = calculateSocialSecurityNumberError(data.socialSecurityNumber)
        if (!!securityError) {
          errors.socialSecurityNumber = securityError
        }
      }

      return errors
    },
    onSubmit: async (data, { setErrors }) => {
      track(trackingEvents.employee.editEmployeeInformationSubmit, {
        fields_edited: [
          ...getDirtyValues(
            Object.entries(form.values),
            Object.fromEntries(Object.entries(form.initialValues))
          ),
        ],
        section_edited: 'basic_information',
        legal_entity_country: legalEntity.country,
        layout_version: 'v2',
      })

      const result = await update({
        ...data,
        id: employee.id,
        accessId: user.id,
        birthdayOn: !!data.birthdayOn ? dateUIToBackend(data.birthdayOn) : null,
      })

      const handled = handleResult(result, (errors) => {
        setErrors({ ...errors })
      })

      if (handled.success) {
        await usersCollection.fetch(), employeesCollection.fetch()
        app.setFlash({
          type: 'correct',
          message: i18n.t('common.saved_successfully'),
        })
        const values = form.values
        form.resetForm({ values })
      }

      return handled
    },
  })

  const handleFieldChanged = (fieldName: string, fieldValue: string | undefined | boolean) => {
    form.setValues({ ...form.values, [fieldName]: fieldValue })
  }

  const handleSubmit = async () => {
    submitIdentifiersEventHandler?.()

    if (!form.dirty) return

    form.handleSubmit()
  }

  const showSubmit =
    canEdit && form.isValid && !submitting && (form.dirty || identifiersDirty) && !identifiersError

  return (
    <FormSection
      title={i18n.t('employee_form.basic_info_section.title')}
      description={i18n.t('employee_form.basic_info_section.description')}
      illustration="id"
      disabled={!showSubmit}
      submitAction={() => handleSubmit()}
    >
      {(loading || submitting) && <Loading />}
      {!loading && !submitting && (
        <Box gap="s24">
          <PersonalSection
            values={form.values}
            onChangeValue={(fieldName, fieldValue) => handleFieldChanged(fieldName, fieldValue)}
            readonly={!canEdit}
            employee={employee}
            errors={form.errors}
          />
          <IdentificationSection
            values={form.values}
            onChangeValue={(fieldName, fieldValue) => handleFieldChanged(fieldName, fieldValue)}
            isSpainCompany={isSpainCompany}
            readonly={!canEdit}
            employee={employee}
            errors={form.errors}
            setIdentifiersDirty={setIdentifiersDirty}
            setIdentifiersError={setIdentifiersError}
            setSubmitEventHandler={setSubmitIdentifiersEventHandler}
            idTypesOptions={idTypesOptions}
          />
        </Box>
      )}
    </FormSection>
  )
}

export default observer(BasicInformation)
